/**
 * Apply scene.json `lighting` block onto the viewer's pc.CameraFrame.
 *
 * The editor authors lighting through a RenderPassCompose pipeline; the
 * viewer renders through pc.CameraFrame. Both ultimately expose the same
 * knobs (tonemap, exposure/brightness, contrast, bloom), so we map the
 * schema block onto CameraFrame's grading/bloom/rendering groups.
 *
 * Schema (documentation/scene-schema.md → "Lighting"):
 *   { tonemap, exposure (EV stops), contrast, bloom{enabled,intensity},
 *     colorEnhance{enabled,shadows,highlights,midtones,vibrance,dehaze} }
 *
 * When the block is absent the viewer keeps its passthrough default
 * (linear tonemap, neutral grading, bloom off) — matches the editor's
 * passthrough mode.
 *
 * colorEnhance: CameraFrame has no shadows/highlights/midtones/dehaze
 * equivalent (those live in the editor-only RenderPassCompose). We map
 * `vibrance` onto grading saturation as a best-effort and skip the rest
 * with a one-line console note rather than silently pretending.
 */

import * as pc from 'playcanvas';

import type { LightingConfig } from './scene-types';

const TONEMAP: Record<string, number> = {
    linear:  pc.TONEMAP_LINEAR,
    hejl:    pc.TONEMAP_HEJL,
    filmic:  pc.TONEMAP_FILMIC,
    aces:    pc.TONEMAP_ACES,
    aces2:   pc.TONEMAP_ACES2,
    neutral: pc.TONEMAP_NEUTRAL
};

// CameraFrame's data groups aren't fully typed in every engine build, so the
// setters go through a loose shape. Each group is feature-detected before use.
interface CameraFrameLike {
    rendering?: { toneMapping?: number };
    grading?: { enabled: boolean; brightness: number; contrast: number; saturation: number };
    bloom?: { intensity: number };
    update(): void;
}

export function applyLighting(
    cameraFrame: CameraFrameLike | null | undefined,
    lighting: LightingConfig | undefined
): void {
    if (!cameraFrame) return;

    // ── Tonemap ───────────────────────────────────────────────────────────
    const tmName = lighting?.tonemap ?? 'linear';
    const tm = TONEMAP[tmName] ?? pc.TONEMAP_LINEAR;
    if (cameraFrame.rendering) cameraFrame.rendering.toneMapping = tm;

    // ── Grading (exposure → brightness, contrast, vibrance → saturation) ──
    if (cameraFrame.grading) {
        const exposure = lighting?.exposure ?? 0;            // EV stops
        const contrast = lighting?.contrast ?? 1;
        const ce = lighting?.colorEnhance;
        const vibrance = ce?.enabled ? (ce.vibrance ?? 0) : 0;

        const brightness = Math.pow(2, exposure);            // EV 0 → 1.0 (neutral)
        const saturation = Math.max(0, 1 + vibrance);        // vibrance -1..1 → sat 0..2

        const nonDefault =
            Math.abs(brightness - 1) > 1e-3 ||
            Math.abs(contrast - 1) > 1e-3 ||
            Math.abs(saturation - 1) > 1e-3;

        cameraFrame.grading.enabled = nonDefault;
        cameraFrame.grading.brightness = brightness;
        cameraFrame.grading.contrast = contrast;
        cameraFrame.grading.saturation = saturation;

        if (ce?.enabled && (ce.shadows || ce.highlights || ce.midtones || ce.dehaze)) {
            console.info(
                '[viewer2] lighting.colorEnhance shadows/highlights/midtones/dehaze ' +
                'have no CameraFrame equivalent — applied vibrance→saturation only.'
            );
        }
    }

    // ── Bloom ─────────────────────────────────────────────────────────────
    if (cameraFrame.bloom) {
        const on = !!lighting?.bloom?.enabled;
        cameraFrame.bloom.intensity = on ? (lighting?.bloom?.intensity ?? 0.03) : 0;
    }

    cameraFrame.update();
}

/**
 * True when the lighting block needs the CameraFrame compose pass: bloom,
 * contrast or colour enhance. Tonemap and exposure the camera does natively,
 * so a scene with only those stays on the 16-bit direct pipeline (which is
 * what makes the viewer match the editor pixel for pixel).
 */
export function lightingNeedsCompose(lighting: LightingConfig | undefined): boolean {
    if (!lighting) return false;
    const bloom = !!lighting.bloom?.enabled && (lighting.bloom?.intensity ?? 0) > 0;
    const contrast = Math.abs((lighting.contrast ?? 1) - 1) > 1e-3;
    const ce = lighting.colorEnhance;
    const enhance = !!ce?.enabled &&
        ((ce.vibrance ?? 0) !== 0 || !!ce.shadows || !!ce.highlights || !!ce.midtones || !!ce.dehaze);
    return bloom || contrast || enhance;
}

/** Tonemap + exposure straight on the camera, for the direct pipeline (no compose pass). */
export function applyLightingNative(app: pc.AppBase, cameraEntity: pc.Entity, lighting: LightingConfig | undefined): void {
    const camera = cameraEntity.camera;
    if (camera) camera.toneMapping = TONEMAP[lighting?.tonemap ?? 'linear'] ?? pc.TONEMAP_LINEAR;
    app.scene.exposure = Math.pow(2, lighting?.exposure ?? 0);
}
